import React from 'react'
import PaginationBar, { paginate, usePersistedPage } from '../../../../../../../components/Common/PaginationBar'

interface Props {
    owners: any[]
    members: any[]
    [key: string]: any
}

const TH = ({ children }: { children: React.ReactNode }) => (
    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 13, color: '#858D9D', fontWeight: 500, background: '#F9FAFB' }}>
        {children}
    </th>
)

const TD = ({ children }: { children: React.ReactNode }) => (
    <td style={{ padding: '12px 16px', fontSize: 14, color: '#344054', borderTop: '1px solid #E4E7EC' }}>
        {children}
    </td>
)

const BADGE: Record<string, React.CSSProperties> = {
    Owner: { background: '#EFF8FF', color: '#175CD3', border: '1px solid #B2DDFF' },
    Member: { background: '#F0FDF9', color: '#107569', border: '1px solid #99F6E0' },
    Guest: { background: '#F9FAFB', color: '#475467', border: '1px solid #E4E7EC' },
}

// AccessRole — Guest 0 · Member 1 · Owner 2 (locked 2026-05-22; see
// place.access.model.js). The API's `owners`/`members` arrays split by GRANT
// DIRECTION — who invited whom — not by access level. Labelling from the array
// therefore called a role-2 Owner a "Member" purely because someone else had
// added them (Navdip Bhai at Radhe Prem Ni Mithas, found 2026-08-18). Label
// from `role`; fall back to the array only for a backend that doesn't send it.
const ROLE_LABEL: Record<number, string> = { 0: 'Guest', 1: 'Member', 2: 'Owner' }
const labelFor = (u: any, fallback: string): string =>
    (typeof u?.role === 'number' && ROLE_LABEL[u.role]) || fallback

const PlaceUsers = ({ owners, members, placeId }: Props) => {
    const [page, setPage] = usePersistedPage(`place-users-${placeId}`)
    const rawRows = [
        ...(owners || []).map((u: any) => ({ ...u, membershipType: labelFor(u, 'Owner') })),
        ...(members || []).map((u: any) => ({ ...u, membershipType: labelFor(u, 'Member') })),
    ]
    // Dedupe by id — owners are listed first, so the owners-array entry wins for
    // the same user. Both entries now carry the same role-derived label anyway.
    const rows = rawRows.filter((u, idx, arr) =>
        u.id == null || arr.findIndex((x: any) => x.id === u.id) === idx
    )
    const { paged, totalPages, safePage } = paginate(rows, page)

    return (
        <div style={{ background: '#fff', border: '1px solid #E4E7EC', borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <TH>Name</TH>
                        <TH>Email</TH>
                        <TH>Membership</TH>
                        <TH>User ID</TH>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={4} style={{ padding: 20, textAlign: 'center', color: '#858D9D', fontSize: 14 }}>
                                No users found for this place
                            </td>
                        </tr>
                    )}
                    {paged.map((user, idx) => (
                        <tr key={user.id || idx}>
                            <TD>
                                {user.firstname
                                    ? `${user.firstname} ${user.lastname || ''}`.trim()
                                    : '--'}
                            </TD>
                            <TD>{user.email || '--'}</TD>
                            <TD>
                                <span style={{
                                    fontSize: 12, fontWeight: 500, padding: '2px 10px',
                                    borderRadius: 12, ...BADGE[user.membershipType],
                                }}>
                                    {user.membershipType}
                                </span>
                            </TD>
                            <TD><code style={{ fontSize: 12 }}>{user.id || '--'}</code></TD>
                        </tr>
                    ))}
                </tbody>
            </table>
            <PaginationBar
                page={safePage}
                totalPages={totalPages}
                total={rows.length}
                onPage={setPage}
            />
        </div>
    )
}

export default PlaceUsers
